import { Link } from "react-router-dom";
import { ArrowRight, Phone, Wind, Cog, Thermometer, Gauge, Droplets, Leaf, SunSnow, CheckCircle2 } from "lucide-react";
import { COMPANY, IMAGES } from "@/lib/site";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";

const CYCLE = [
    { icon: Wind, n: "01", title: "Absorb", text: "The outdoor unit draws in outside air and absorbs its naturally occurring heat energy into a refrigerant — even in cold UK weather." },
    { icon: Cog, n: "02", title: "Upgrade", text: "A compressor raises the temperature of that heat to a level suitable for home heating and hot water." },
    { icon: Thermometer, n: "03", title: "Deliver", text: "The heat is transferred into your home's heating system and hot-water cylinder." },
];

const BENEFITS = [
    { icon: Gauge, title: "Efficient Heating", text: "Heat pumps can deliver multiple units of heat energy for each unit of electricity used." },
    { icon: Droplets, title: "Heating & Hot Water", text: "A correctly designed system can provide both space heating and domestic hot water." },
    { icon: Leaf, title: "Lower-Carbon Technology", text: "Reduce reliance on fossil-fuel heating." },
    { icon: SunSnow, title: "Year-Round Operation", text: "Modern air source heat pumps are designed to operate throughout the UK heating season." },
];

const DESIGN_FACTORS = [
    "A room-by-room heat-loss calculation for the property",
    "Radiator and heat-emitter capacity at lower flow temperatures",
    "Hot-water demand and cylinder specification",
    "Insulation levels and the building fabric",
    "Outdoor unit positioning and the existing heating system",
];

export default function HeatPumps() {
    return (
        <>
            <SEO
                title="Air Source Heat Pumps | Virtex Renewables"
                description="How air source heat pumps work, the benefits of low-carbon heating, and why correct system design matters for UK homes. Virtex Renewables — England & Wales."
            />

            <section className="relative overflow-hidden bg-slate-50 py-16 md:py-24" data-testid="heat-pumps-hero">
                <div className="pointer-events-none absolute -right-40 -top-40 h-[26rem] w-[26rem] rounded-full bg-emerald-100/70 blur-3xl" />
                <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
                    <Reveal>
                        <span className="inline-block rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
                            Heat Pump Technology
                        </span>
                        <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
                            Air Source Heat Pumps
                        </h1>
                        <p className="mt-5 text-base leading-relaxed text-slate-600">
                            An air source heat pump extracts heat energy from the outside air and transfers it into
                            your home's heating and hot-water system. Unlike traditional gas and oil boilers, heat
                            pumps do not generate heat through on-site fuel combustion — they use electricity to
                            transfer naturally occurring heat from the outside air into your home.
                        </p>
                        <p className="mt-4 text-base leading-relaxed text-slate-600">
                            When properly designed for the property, an air source heat pump can provide an efficient,
                            low-carbon alternative to traditional fossil-fuel heating.
                        </p>
                        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                            <Link
                                to="/contact#enquiry-form"
                                data-testid="heat-pumps-assessment-button"
                                className="group flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-emerald-700"
                            >
                                Get a Free Assessment
                                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                            </Link>
                            <a
                                href={COMPANY.phoneHref}
                                data-testid="heat-pumps-call-button"
                                className="flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-bold text-slate-900 transition-colors duration-200 hover:border-emerald-600 hover:text-emerald-700"
                            >
                                <Phone className="h-4 w-4 text-emerald-600" /> {COMPANY.phoneDisplay}
                            </a>
                        </div>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <img
                            src={IMAGES.heatPumpUnit}
                            alt="Air source heat pump outdoor unit installed outside a UK brick home"
                            className="aspect-[4/3] w-full rounded-3xl object-cover shadow-2xl"
                            loading="eager"
                        />
                    </Reveal>
                </div>
            </section>

            <section className="bg-white py-16 md:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="mx-auto max-w-2xl text-center">
                        <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                            From Outside Air to Home Heating
                        </h2>
                        <p className="mt-4 text-base leading-relaxed text-slate-600">
                            A heat pump works a little like a refrigerator in reverse — moving heat from one place to
                            another rather than burning fuel to create it.
                        </p>
                    </Reveal>
                    <div className="mt-14 grid gap-5 md:grid-cols-3">
                        {CYCLE.map((s, i) => (
                            <Reveal key={s.n} delay={i * 0.1}>
                                <div className="h-full rounded-2xl border border-slate-200 bg-slate-50 p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`cycle-card-${i}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-600 shadow-lg shadow-emerald-600/25">
                                            <s.icon className="h-5 w-5 text-white" />
                                        </div>
                                        <span className="font-display text-3xl font-extrabold text-slate-200">{s.n}</span>
                                    </div>
                                    <h3 className="mt-5 font-display text-xl font-semibold text-slate-900">{s.title}</h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{s.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {BENEFITS.map((b, i) => (
                            <Reveal key={b.title} delay={i * 0.08}>
                                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`heat-pumps-benefit-${i}`}>
                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50">
                                        <b.icon className="h-5 w-5 text-emerald-600" />
                                    </div>
                                    <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{b.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{b.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-slate-50 py-16 md:py-24">
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
                    <Reveal className="order-2 lg:order-1">
                        <img
                            src={IMAGES.townhouse}
                            alt="Traditional UK brick home of the type assessed for air source heat pump suitability"
                            className="aspect-[4/3] w-full rounded-3xl object-cover shadow-xl"
                            loading="lazy"
                        />
                    </Reveal>
                    <Reveal delay={0.1} className="order-1 lg:order-2">
                        <span className="inline-block rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">
                            System Design
                        </span>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                            Why Design Matters More Than the Unit
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-slate-600">
                            Heat pumps run at lower flow temperatures than gas or oil boilers, so performance depends
                            on how well the whole system is matched to the property. Two identical houses can need
                            different systems — which is why we design around the building, not a brochure.
                        </p>
                        <ul className="mt-7 space-y-3.5">
                            {DESIGN_FACTORS.map((f) => (
                                <li key={f} className="flex items-start gap-3 text-sm font-medium leading-relaxed text-slate-700">
                                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                                    {f}
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>
            </section>

            <CTABand />
        </>
    );
}
